import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Lightbulb, Target } from 'lucide-react';

export function ProjectScoutPage() {
  return (
    <section
      id="project-scout"
      className="min-h-screen scroll-mt-24 bg-neutral-950 pb-24 pt-32 text-neutral-100 md:pt-36"
    >
      <div className="mx-auto w-full max-w-5xl px-4 md:px-8">
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white transition hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Link>

        <p className="mt-8 text-xs uppercase tracking-[0.22em] text-white">
          Project
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
          The Scout
        </h1>
        <p className="mt-4 max-w-3xl text-white md:text-lg">
          An autonomous ground vehicle built for persistent patrol across spaces that
          fixed cameras and human teams cannot cover effectively.
        </p>

        <div className="mt-10 overflow-hidden rounded-xl border border-white/15 bg-neutral-900">
          <img
            src="/rover1.png"
            alt="Valinor Dynamics Scout rover"
            className="mx-auto h-80 w-auto object-contain p-8"
          />
        </div>

        <div className="mt-12 space-y-10">
          <DetailBlock
            Icon={AlertTriangle}
            label="Problem"
            title="Static surveillance leaves blind spots"
          >
            <p>
              National parks, borders, residential properties, and industrial sites all
              rely on fixed cameras and limited human patrols. Cameras have predictable
              blind spots, and patrols cannot be everywhere at once. Intruders, poachers,
              and unsafe conditions go unnoticed until it is too late to act.
            </p>
          </DetailBlock>

          <DetailBlock
            Icon={Lightbulb}
            label="Solution"
            title="A mobile, autonomous patrol platform"
          >
            <p>
              The Scout combines top-mounted LiDAR for spatial awareness, a forward
              camera for live vision, and a Raspberry Pi compute stack driving a
              four-wheel base. It moves through routes a static camera cannot reach,
              streams telemetry back to operators, and flags abnormalities in real time.
            </p>
            <p className="mt-3">
              Because the platform is mobile, blind spots shift and shrink as the Scout
              moves, making it harder for threats to find and exploit predictable gaps in
              coverage.
            </p>
          </DetailBlock>

          <DetailBlock
            Icon={Target}
            label="Impact"
            title="Faster response, broader coverage, fewer gaps"
          >
            <p>
              The Scout extends the reach of small security teams without adding manual
              patrol load. Rangers, security operators, and defense personnel get earlier
              alerts with better context, allowing them to respond before incidents
              escalate.
            </p>
            <p className="mt-3">
              The result is broader area coverage, faster verification of threats, and a
              meaningful reduction in the time between an event happening and a human
              acting on it.
            </p>
          </DetailBlock>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link
            to="/scout"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white px-5 py-2.5 text-sm font-semibold text-black transition hover:bg-neutral-200"
          >
            Explore the Scout anatomy
          </Link>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 rounded-md border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition hover:border-white/40 hover:text-white"
          >
            Back to Projects
          </Link>
        </div>
      </div>
    </section>
  );
}

interface DetailBlockProps {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  title: string;
  children: React.ReactNode;
}

function DetailBlock({ Icon, label, title, children }: DetailBlockProps) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-6 md:p-8">
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-neutral-500">
        <Icon className="h-5 w-5 text-white" />
        {label}
      </div>
      <h3 className="mt-3 text-xl font-semibold tracking-tight md:text-2xl">{title}</h3>
      <div className="mt-4 text-sm leading-7 text-white md:text-base md:leading-8">
        {children}
      </div>
    </div>
  );
}
